import { useCallback, useEffect, useState } from "react";
import { ApiError, apiRequest, authToken } from "@/lib/api";

type Employee = { id: number; name: string; email: string; role: string };
type Submission = {
  id: number;
  form_type: string;
  full_name: string | null;
  email: string;
  phone: string | null;
  organization: string | null;
  status: string;
  notification_status: string;
  submitted_at: string;
};
type SubmissionDetail = Submission & {
  payload: Record<string, string>;
  employee_notes: string | null;
  assigned_to_name: string | null;
};

const labels: Record<string, string> = {
  contact: "Contact",
  newsletter: "Newsletter",
  academy: "Academy",
  advisory: "Advisory",
  partnership: "Partnership",
};

export default function AdminPage() {
  const [user, setUser] = useState<Employee | null>(null);
  const [checking, setChecking] = useState(true);
  const [loginError, setLoginError] = useState("");
  const [items, setItems] = useState<Submission[]>([]);
  const [selected, setSelected] = useState<SubmissionDetail | null>(null);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [status, setStatus] = useState("");
  const [formType, setFormType] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const logout = useCallback(async () => {
    try { await apiRequest("/auth/logout", { method: "POST", body: "{}" }); } catch { /* clear locally */ }
    authToken.clear();
    setUser(null);
    setSelected(null);
  }, []);

  const handleAuthError = useCallback((error: unknown) => {
    if (error instanceof ApiError && error.status === 401) logout();
  }, [logout]);

  const loadData = useCallback(async () => {
    if (!authToken.get()) return;
    setLoading(true);
    const query = new URLSearchParams();
    if (status) query.set("status", status);
    if (formType) query.set("form_type", formType);
    if (search) query.set("search", search);
    try {
      const [list, dashboard] = await Promise.all([
        apiRequest<{ items: Submission[] }>(`/submissions?${query}`),
        apiRequest<{ counts: Record<string, number> }>("/dashboard/stats"),
      ]);
      setItems(list.items);
      setStats(dashboard.counts);
    } catch (error) {
      handleAuthError(error);
      if (!(error instanceof ApiError && error.status === 401)) setMessage("Unable to load submissions.");
    } finally {
      setLoading(false);
    }
  }, [formType, handleAuthError, search, status]);

  useEffect(() => {
    if (!authToken.get()) { setChecking(false); return; }
    apiRequest<{ user: Employee }>("/auth/me")
      .then(({ user }) => setUser(user))
      .catch(() => authToken.clear())
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  async function login(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoginError("");
    const form = event.currentTarget;
    const values = new FormData(form);
    try {
      const result = await apiRequest<{ token: string; user: Employee }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: values.get("email"), password: values.get("password") }),
      });
      authToken.set(result.token);
      setUser(result.user);
      form.reset();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Unable to sign in.");
    }
  }

  async function openSubmission(id: number) {
    try {
      const result = await apiRequest<{ submission: SubmissionDetail }>(`/submissions/${id}`);
      setSelected(result.submission);
    } catch (error) { handleAuthError(error); }
  }

  async function saveSubmission(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const values = new FormData(event.currentTarget);
    try {
      await apiRequest(`/submissions/${selected.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          status: values.get("status"),
          employee_notes: values.get("employee_notes"),
          assign_to_me: values.get("assign_to_me") === "on",
        }),
      });
      setMessage("Submission updated.");
      await openSubmission(selected.id);
      await loadData();
    } catch (error) {
      handleAuthError(error);
      setMessage(error instanceof Error ? error.message : "Update failed.");
    }
  }

  if (checking) return <div className="min-h-screen bg-[#F8F6F3] grid place-items-center text-[#1A4D3C]">Checking session…</div>;

  if (!user) {
    return (
      <main className="min-h-screen bg-[#F8F6F3] grid place-items-center p-6 font-['Inter']">
        <div className="w-full max-w-md bg-white border border-[#E8E4DC] rounded-xl p-8 shadow-lg">
          <img src="/assets/brand/logo.png" alt="Harambee Global Ventures" className="h-20 w-auto mx-auto mb-6" />
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-bold text-center text-[#2B2D2F]">Employee Portal</h1>
          <p className="text-sm text-[#6B6B6B] text-center mt-2 mb-7">Sign in to review website submissions.</p>
          <form onSubmit={login} className="space-y-4">
            <label className="block text-sm font-semibold text-[#2B2D2F]">Email
              <input name="email" type="email" required autoComplete="username" className="mt-2 w-full rounded-md border border-[#E8E4DC] px-4 py-3 focus:outline-none focus:border-[#1A4D3C]" />
            </label>
            <label className="block text-sm font-semibold text-[#2B2D2F]">Password
              <input name="password" type="password" required autoComplete="current-password" className="mt-2 w-full rounded-md border border-[#E8E4DC] px-4 py-3 focus:outline-none focus:border-[#1A4D3C]" />
            </label>
            {loginError && <p className="text-sm text-red-700 bg-red-50 rounded-md p-3">{loginError}</p>}
            <button className="w-full rounded-md bg-[#1A4D3C] text-white py-3 font-semibold hover:bg-[#0D2E1E]">Sign In</button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F8F6F3] font-['Inter'] text-[#2B2D2F]">
      <header className="bg-[#1A2B1F] text-white px-6 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-4"><img src="/assets/brand/logo.png" alt="" className="h-12 w-auto" /><div><h1 className="font-semibold">Employee Portal</h1><p className="text-xs text-white/60">{user.name} · {user.role}</p></div></div>
          <button onClick={logout} className="border border-white/20 rounded-md px-4 py-2 text-sm hover:border-[#C9A961]">Sign Out</button>
        </div>
      </header>
      <div className="max-w-[1400px] mx-auto p-6 lg:p-8">
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[["All", stats.total], ["New", stats.new_count], ["In progress", stats.in_progress_count], ["Last 7 days", stats.last_7_days]].map(([label, value]) => (
            <div key={String(label)} className="bg-white border border-[#E8E4DC] rounded-lg p-5"><p className="text-xs uppercase tracking-wider text-[#6B6B6B]">{label}</p><p className="text-3xl font-semibold text-[#1A4D3C] mt-1">{Number(value || 0)}</p></div>
          ))}
        </section>
        {message && <button onClick={() => setMessage("")} className="w-full text-left mb-4 bg-[#C9A961]/20 border border-[#C9A961]/40 rounded-md p-3 text-sm">{message}</button>}
        <section className="bg-white border border-[#E8E4DC] rounded-lg overflow-hidden">
          <div className="p-4 border-b border-[#E8E4DC] grid sm:grid-cols-3 gap-3">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, organization" className="border rounded-md px-3 py-2 text-sm" />
            <select value={formType} onChange={(e) => setFormType(e.target.value)} className="border rounded-md px-3 py-2 text-sm"><option value="">All forms</option>{Object.entries(labels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-md px-3 py-2 text-sm"><option value="">All statuses</option><option value="new">New</option><option value="in_progress">In progress</option><option value="resolved">Resolved</option><option value="archived">Archived</option></select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm"><thead className="bg-[#F8F6F3] text-left"><tr><th className="p-3">Submitted</th><th className="p-3">Type</th><th className="p-3">Contact</th><th className="p-3">Organization</th><th className="p-3">Status</th></tr></thead>
              <tbody>{items.map((item) => <tr key={item.id} onClick={() => openSubmission(item.id)} className="border-t border-[#E8E4DC] hover:bg-[#F8F6F3] cursor-pointer"><td className="p-3 whitespace-nowrap">{new Date(item.submitted_at.replace(" ", "T")).toLocaleString()}</td><td className="p-3">{labels[item.form_type]}</td><td className="p-3"><span className="font-medium">{item.full_name || "—"}</span><br/><span className="text-[#6B6B6B]">{item.email}</span></td><td className="p-3">{item.organization || "—"}</td><td className="p-3"><span className="rounded-full bg-[#1A4D3C]/10 px-2 py-1 text-xs">{item.status.replace("_", " ")}</span></td></tr>)}</tbody>
            </table>
            {!loading && items.length === 0 && <p className="p-10 text-center text-[#6B6B6B]">No submissions match these filters.</p>}
            {loading && <p className="p-10 text-center text-[#6B6B6B]">Loading…</p>}
          </div>
        </section>
      </div>
      {selected && <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setSelected(null)}><aside className="w-full max-w-xl h-full overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between gap-4 mb-5"><div><p className="text-xs uppercase text-[#C9A961] font-semibold">{labels[selected.form_type]} #{selected.id}</p><h2 className="text-2xl font-semibold">{selected.full_name || selected.email}</h2></div><button onClick={() => setSelected(null)} aria-label="Close" className="text-2xl">×</button></div>
        <dl className="grid grid-cols-1 gap-3 mb-7">{Object.entries(selected.payload).map(([key, value]) => <div key={key} className="border-b pb-3"><dt className="text-xs uppercase tracking-wider text-[#6B6B6B]">{key.replaceAll("_", " ")}</dt><dd className="mt-1 whitespace-pre-wrap break-words">{value || "—"}</dd></div>)}</dl>
        <form onSubmit={saveSubmission} className="space-y-4 bg-[#F8F6F3] rounded-lg p-5">
          <label className="block text-sm font-semibold">Status<select name="status" defaultValue={selected.status} className="mt-2 w-full border rounded-md p-3 font-normal"><option value="new">New</option><option value="in_progress">In progress</option><option value="resolved">Resolved</option><option value="archived">Archived</option></select></label>
          <label className="block text-sm font-semibold">Employee notes<textarea name="employee_notes" defaultValue={selected.employee_notes || ""} rows={5} className="mt-2 w-full border rounded-md p-3 font-normal" /></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="assign_to_me" /> Assign this submission to me</label>
          {selected.assigned_to_name && <p className="text-xs text-[#6B6B6B]">Currently assigned to {selected.assigned_to_name}</p>}
          <button className="w-full bg-[#1A4D3C] text-white rounded-md py-3 font-semibold">Save Changes</button>
        </form>
      </aside></div>}
    </main>
  );
}
